import { recordBranchEvent, type PublicBranchItem } from "@workspace/api-client-react";
import { Phone, MessageCircle, Navigation, Mail } from "lucide-react";
import type { ReactNode } from "react";

type EventType = "call" | "whatsapp" | "directions";

export function whatsappDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

function track(handle: string, slug: string | null, type: EventType) {
  if (!slug) return;
  try {
    recordBranchEvent({ handle, branchSlug: slug, type }, { keepalive: true }).catch(() => {});
  } catch {
    /* never block the visitor */
  }
}

const btn =
  "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface Props {
  branch: PublicBranchItem;
  brandHandle: string;
  includeEmail?: boolean;
}

export function BranchActions({ branch, brandHandle, includeEmail = false }: Props) {
  const wa = whatsappDigits(branch.whatsapp);
  const items: ReactNode[] = [];
  if (branch.phone) {
    items.push(
      <a key="call" href={`tel:${branch.phone.replace(/[^\d+]/g, "")}`} className={btn}
        aria-label={`Call ${branch.name}`} onClick={() => track(brandHandle, branch.slug, "call")}>
        <Phone className="h-4 w-4" aria-hidden="true" /> Call
      </a>,
    );
  }
  if (wa) {
    items.push(
      <a key="wa" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className={btn}
        aria-label={`WhatsApp ${branch.name}`} onClick={() => track(brandHandle, branch.slug, "whatsapp")}>
        <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
      </a>,
    );
  }
  if (includeEmail && branch.email) {
    items.push(
      <a key="email" href={`mailto:${branch.email}`} className={btn} aria-label={`Email ${branch.name}`}>
        <Mail className="h-4 w-4" aria-hidden="true" /> Email
      </a>,
    );
  }
  if (branch.routable && branch.directionsUrl) {
    items.push(
      <a key="dir" href={branch.directionsUrl} target="_blank" rel="noopener noreferrer" className={btn}
        aria-label={`Directions to ${branch.name}`} onClick={() => track(brandHandle, branch.slug, "directions")}>
        <Navigation className="h-4 w-4" aria-hidden="true" /> Directions
      </a>,
    );
  }
  if (!items.length) return null;
  return <div className="flex flex-wrap gap-2">{items}</div>;
}
